
import {computed, defineComponent, onMounted, onUnmounted, reactive, ref, toRefs} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import InvoiceStateDTO from "@/models/InvoiceStateDTO";
import {numberFormatter} from "@/service/NumberFormatter"
import {paymentMethodsHelper} from "@/service/PaymentMethodsHelper"
import APIClient from "@/service/APIClient";
import {DealStatus} from "@/enum/DealStatus";
import DealDTO from "@/models/DealDTO";
import {FlowType} from "@/enum/FlowType";
import moment from "moment/moment";
import {InvoiceStatus} from "@/enum/InvoiceStatus";
import axios from "axios";
import {useI18n} from 'vue-i18n'
import DefaultPaymentDetails from "@/components/DefaultPaymentDetails.vue";
import SBPQRPaymentDetails from "@/components/SBPQRPaymentDetails.vue";
import {dealHasSbpQrPayload} from "@/service/sbpQrDeal";
import BankPayButtonPaymentDetails from "@/components/BankPayButtonPaymentDetails.vue";
import {event} from 'vue-gtag'
import {useDropzone} from "vue3-dropzone";
import InvoiceStateInfo from "@/components/InvoiceStateInfo.vue";
import copyTextToClipboard from "@/service/CopyHelper";
import ToBankDetailsPaymentDetails from "@/components/ToBankDetailsPaymentDetails.vue";
import ClickQRPaymentDetails from "@/components/ClickQRPaymentDetails.vue";
import AcquiringPaymentDetails from "@/components/AcquiringPaymentDetails.vue";
import TPayButton from "@/components/PayButtons/TPayButton.vue";
import {formatCancelTime} from "@/service/helper";
import TPayMatching from "@/components/TPayMatching.vue";
import PSBCrossBorderPage from "@/components/PSBCrossBorderPage.vue";
import AlfaCrossBorderPage from "@/components/AlfaCrossBorderPage.vue";
import AlfaC2cCrossBorderPage from "@/components/AlfaC2cCrossBorderPage.vue";
import VaslTransferPage from "@/components/VaslTransferPage.vue";
import KazakhstanCrossBorderPage from "@/components/KazakhstanCrossBorderPage.vue";
import PayButtonsPage from "@/components/PayButtonsPage.vue";
import PaymentVariantDTO from "@/models/PaymentVariantDTO";
import {isBankPayFlow} from "@/service/bankPayFlows";


const apiUrl = window.API_URL;

const api = new APIClient();

export default defineComponent({
  name: 'Invoice',
  components: {
    PSBCrossBorderPage,
    AlfaCrossBorderPage,
    AlfaC2cCrossBorderPage,
    VaslTransferPage,
    KazakhstanCrossBorderPage,
    TPayMatching,
    PayButtonsPage,
    TPayButton,
    ClickQRPaymentDetails,
    ToBankDetailsPaymentDetails,
    InvoiceStateInfo,
    BankPayButtonPaymentDetails,
    SBPQRPaymentDetails,
    DefaultPaymentDetails,
    AcquiringPaymentDetails
  },
  setup() {
    const state = reactive({
      deeplinkClicked: false as boolean,
      copyHintTimeout: null as number|null,
      copyValue: '' as string,
      invoice: null as InvoiceStateDTO|null,
      availableMethods: null as string[]|null,
      availableVariants: null as PaymentVariantDTO[]|null,
      currentTimestamp: Date.now(),
      payMethod: null as string|null,
      currentDeal: null as DealDTO|null,
      updateCurrentTimeInterval: null as number|null,
      interval: null as number|null,
      disputeAttachmentForm: null as FormData|null,
      waitingForRequisites: true,
      startDealCount: 0,
      transactionId: '' as string,
      transactionIdError: '' as string,
      userPhone: '' as string,
      userPhoneError: '' as string,
      userPhoneSubmitted: false,
      receiptFile: null as File|null,
      receiptError: '' as string
    });

    const PHONE_REQUISITES_WAIT_MS = 30000
    let userPhoneDealStarted = false
    let userPhoneWaitStartedAt: number | null = null

    const { locale, t } = useI18n({ useScope: 'global' })
    const route = useRoute()
    const router = useRouter()
    const disputeAttachment = ref(["file"])


    const loadInvoiceData = async (invoiceId: string): Promise<InvoiceStateDTO> =>  {
      const ua = navigator.userAgent
      const invoice =  await api.getInvoice(invoiceId, ua);
      applyFlowOptions(invoice.flowData?.options);

      if (state.invoice && invoice.status === InvoiceStatus.PAID && state.invoice.status !== InvoiceStatus.PAID) {
        event('invoicePaid', {})
      }
      state.invoice = invoice
      if (state.invoice.deals.length > 0) {
        state.currentDeal = state.invoice.deals[0]
        state.payMethod = state.currentDeal.paymentMethod
        state.availableMethods = [state.currentDeal.paymentMethod]
        await selectPayMethod(state.payMethod)
      }

      return invoice
    }

    const applyFlowOptions = (options: any) => {
      if (!options || Array.isArray(options)) {
        return
      }

      const theme = options.theme ?? 'light'
      if (theme && ['light', 'dark'].includes(theme)) {
        document.documentElement.setAttribute('data-theme', theme);
      }
    }

    const openDispute = async (invoiceId: string) => {
      state.invoice = await api.openDispute(invoiceId, state.disputeAttachmentForm!)
    }

    const setDisputeAttachment = async (invoiceId: string) => {
      state.invoice = await api.setDisputeAttachment(invoiceId, state.disputeAttachmentForm!)
    }

    const addAttachment = (e:any) => {
      state.disputeAttachmentForm = new FormData()
      state.disputeAttachmentForm.append('attachment', e.target.files[0], e.target.files[0].name)
      state.disputeAttachmentForm.append('dealId', state.currentDeal!.id)
    }

    const loadAvailableMethods = async (invoiceId: string) => {
      state.availableMethods = await api.getAvailableMethods(invoiceId);
    }

    const loadAvailableVariants = async (invoiceId: string) => {
      try {
        state.availableVariants = await api.getAvailablePaymentVariants(invoiceId);
      } catch {
        state.availableVariants = [];
      }
    }

    const findDeal = (id: string): DealDTO | null => {
      if (state.invoice === null) {
        return null
      }

      let deal = state.invoice.deals.find((deal) => deal.id === id);
      return deal ? deal : null
    }

    const updateCurrentTime = () => {
      state.currentTimestamp = Date.now()
    }

    const getTimeToPayPercent = (deal: DealDTO): number => {
      const canceledAt = moment(new Date(deal.canceledAt)).unix() * 1000;
      const createdAt = moment(new Date(deal.createdAt)).unix() * 1000;

      const total = (canceledAt - state.currentTimestamp)/(canceledAt - createdAt) * 100;
      if (total > 100) {
        return 100;
      }

      if (total < 0) {
        return 0;
      }

      return total;
    }

    const isExpired = (deal: DealDTO): boolean => {
      const canceledAt = moment(new Date(deal.canceledAt));

      return canceledAt.unix() * 1000 - state.currentTimestamp <= 0;
    }


    const cssFlowClassName = computed(() => {
      if (isBankPayFlow(flowType.value) || flowType.value === FlowType.DEFAULT) {
        return 'style-v2'
      }

      return ''
    })

    const receiptPdfRequired = computed(() => state.invoice?.flowData?.options?.receiptPdfRequired === true);

    const onReceiptDrop = (acceptFiles: File[], rejectReasons: any[]) => {
      if (acceptFiles.length > 0) {
        state.receiptFile = acceptFiles[0];
        state.receiptError = '';
        return;
      }

      if (rejectReasons.length > 0) {
        state.receiptError = t('invoice.receipt-pdf.only-pdf') as string;
      }
    }

    const receiptDropzone = useDropzone({ onDrop: onReceiptDrop, accept: ['.pdf', 'application/pdf'], maxFiles: 1, multiple: false });

    const removeReceipt = () => {
      state.receiptFile = null;
      state.receiptError = '';
    }

    const showTpayWarning = computed(() => {
      return state.currentDeal !== null
        && flowType.value === FlowType.TPAY
        && state.invoice?.flowData?.options?.showTpayWarning === true
    })

    const showAmountCopyButton = computed(() => {
      return !(flowType.value === FlowType.VIET_QR
        && state.invoice?.flowData?.options?.vietQrMobileSaveFlow === true)
    })

    const currentUrl = computed(() => {
      return (new URL(document.location.href))
    })

    const isAcsReturn = computed(() => {
      const marker = route.query['3ds'];
      if (Array.isArray(marker)) {
        return marker.includes('1');
      }
      return marker === '1';
    })

    const flowType = computed(() => {
      if (state.invoice?.flow === FlowType.NSPK || currentUrl.value?.searchParams.get('flow') === FlowType.NSPK) {
        return FlowType.NSPK
      }
      if (state.currentDeal?.paymentOption === "SBP_QR") {
        return FlowType.SBP_QR
      }

      if (state.currentDeal?.paymentOption === "MANUAL_SBP_QR" || state.invoice?.paymentOption === "MANUAL_SBP_QR") {
        return FlowType.MANUAL_SBP_QR
      }

      if (state.currentDeal?.paymentOption === 'CLICK_UZ_QR') {
        return FlowType.CLICK_QR
      }

      if (state.currentDeal?.paymentOption === 'VIET_QR') {
        return FlowType.VIET_QR
      }

      if (state.currentDeal?.paymentOption === 'IND_QR') {
        return FlowType.IND_QR
      }

      if (state.currentDeal?.paymentOption === 'BR_QR') {
        return FlowType.BR_QR
      }

      if (state.currentDeal?.paymentOption === "TO_BANK_DETAILS") {
        return FlowType.TO_BANK_DETAILS
      }

      if (state.currentDeal?.paymentOption === "CARD_ACQUIRING" || state.invoice?.paymentOption === "CARD_ACQUIRING") {
        return FlowType.CARD_ACQUIRING
      }

      if (state.currentDeal?.paymentOption === "MANUAL_ECOM" || state.invoice?.paymentOption === "MANUAL_ECOM") {
        return FlowType.MANUAL_ECOM
      }

      if (
        state.currentDeal?.paymentOption === "CARD_ACQUIRING_REDIRECT"
        || state.invoice?.paymentOption === "CARD_ACQUIRING_REDIRECT"
        || state.availableVariants?.some(v => v.paymentOption === "CARD_ACQUIRING_REDIRECT")
      ) {
        return FlowType.CARD_ACQUIRING_REDIRECT
      }

      if (state.invoice?.flow) {
        return state.invoice.flow;
      }

      return FlowType.DEFAULT
    })

    const KNOWN_PAYMENT_PAGE_LAYOUTS = ['seq', 'pay_buttons', 'psb_cross_border', 'alfa_cross_border', 'alfa_c2c_cross_border', 'psb_vasl_tjs', 'gazprom_vasl_tjs', 'kazakhstan_cross_border']

    const legacyPaymentPageLayout = computed(() => {
      if (state.invoice?.flowData?.options?.psbCrossBorderDesign === true) {
        return 'psb_cross_border'
      }

      const theme = state.invoice?.flowData?.options?.theme
      if (theme === 'seq' || theme === 'pay_buttons') {
        return theme
      }

      return 'default'
    })

    const paymentPageLayout = computed(() => {
      const layout = state.invoice?.flowData?.layout
      if (typeof layout !== 'string' || layout === '') {
        return legacyPaymentPageLayout.value
      }

      if (layout === 'default' || KNOWN_PAYMENT_PAGE_LAYOUTS.includes(layout)) {
        return layout
      }

      return 'default'
    })

    const psbCrossBorderDeal = computed((): DealDTO | null => {
      if (paymentPageLayout.value !== 'psb_cross_border') {
        return null
      }
      return state.currentDeal ?? state.invoice?.deals[0] ?? null
    })

    const alfaCrossBorderDeal = computed((): DealDTO | null => {
      if (paymentPageLayout.value !== 'alfa_cross_border') {
        return null
      }
      return state.currentDeal ?? state.invoice?.deals[0] ?? null
    })

    const alfaC2cCrossBorderDeal = computed((): DealDTO | null => {
      if (paymentPageLayout.value !== 'alfa_c2c_cross_border') {
        return null
      }
      return state.currentDeal ?? state.invoice?.deals[0] ?? null
    })

    const vaslTransferBank = computed((): 'psb' | 'gazprom' | null => {
      switch (paymentPageLayout.value) {
        case 'psb_vasl_tjs': return 'psb'
        case 'gazprom_vasl_tjs': return 'gazprom'
        default: return null
      }
    })

    const vaslTransferDeal = computed((): DealDTO | null => {
      if (vaslTransferBank.value === null) {
        return null
      }
      return state.currentDeal ?? state.invoice?.deals[0] ?? null
    })

    const kazakhstanCrossBorderDeal = computed((): DealDTO | null => {
      if (paymentPageLayout.value !== 'kazakhstan_cross_border') {
        return null
      }
      return state.currentDeal ?? state.invoice?.deals[0] ?? null
    })

    const showSbpQrPaymentDetails = computed(
      () => flowType.value === FlowType.SBP_QR || flowType.value === FlowType.MANUAL_SBP_QR
    )

    const manualSbpQrAwaitingRequisites = computed(() => {
      if (flowType.value !== FlowType.MANUAL_SBP_QR || state.currentDeal === null) {
        return false
      }
      return !dealHasSbpQrPayload(state.currentDeal)
    })

    const title = computed(() => {
      switch (flowType.value) {
        case FlowType.DEFAULT:
        case FlowType.SBP_QR:
          switch (state.currentDeal?.paymentOption ?? state.invoice?.paymentOption) {
            case 'SBP': return 'invoice.title_sbp';
            case 'CVU': return 'invoice.title_CVU';
          }
          if (state.currentDeal?.paymentSystem && ['HUMO', 'UZCARD'].includes(state.currentDeal.paymentSystem)) {
            return `invoice.title_${state.currentDeal.paymentSystem}`;
          }
          return 'invoice.title';
        default:
          return `invoice.flow_titles.${flowType.value}`;
      }
    })

    const singleDealStartAttempt = computed(() => state.invoice?.flowData?.options?.singleDealStartAttempt === true)

    const findActiveDeal = (method: string | null): DealDTO | null => {
      if (state.invoice === null) {
        return null
      }

      let active = state.invoice.deals.find((deal) => deal.isActive && deal.paymentMethod === method);
      return active ? active : null
    }

    const replacePayMethodQuery = (method: string | null) => {
      if (state.invoice === null) {
        return
      }

      router.replace({
        path: route.path,
        query: {
          id: state.invoice.id,
          method: method,
          lang: locale.value as string,
          ...(isAcsReturn.value ? { '3ds': '1' } : {}),
        }
      });
    }

    const selectPayMethod = async (method: string | null) => {
      if (state.invoice === null || state.invoice.status === InvoiceStatus.PAID) {
        return;
      }

      if (flowType.value === FlowType.NSPK) {
        return;
      }

      if (singleDealStartAttempt.value && state.invoice.deals.length === 0 && state.invoice.status !== InvoiceStatus.NEW) {
        return;
      }

      const activeDeal = findActiveDeal(method);
      const paymentOption = state.availableVariants?.find(v => v.paymentMethod === method)?.paymentOption
        ?? state.invoice.paymentOption;
      const startsDeal = activeDeal === null
        && (!singleDealStartAttempt.value || state.invoice.deals.length === 0)
        && paymentOption !== "CARD_ACQUIRING"
        && paymentOption !== "MANUAL_ECOM"
        && paymentOption !== "CARD_ACQUIRING_REDIRECT";
      const deferRouteReplace = activeDeal === null
        && (state.userPhoneSubmitted || (singleDealStartAttempt.value && startsDeal));

      if (state.userPhoneSubmitted && userPhoneDealStarted && activeDeal === null) {
        return;
      }

      if (!deferRouteReplace) {
        replacePayMethodQuery(method);
      }

      state.payMethod = method;

      if (startsDeal) {
        if (state.userPhoneSubmitted) {
          userPhoneDealStarted = true
        }
        const invoiceId = state.invoice.id
        try {
          state.invoice = await api.startDeal(
            invoiceId,
            method,
            userPhoneDigits.value !== '' ? {userPhone: userPhoneDigits.value} : undefined
          );
        } catch (e) {
          state.availableMethods = [];
          state.payMethod = null
          if (state.userPhoneSubmitted) {
            state.waitingForRequisites = false
          }
          if (singleDealStartAttempt.value) {
            try {
              await loadInvoiceData(invoiceId)
            } catch (e) {
            }
          }
          return;
        }

      }

      state.currentDeal = findActiveDeal(method);
      if (deferRouteReplace && state.currentDeal !== null) {
        replacePayMethodQuery(method);
      }
    }

    const handleDealCreated = async (invoice: InvoiceStateDTO) => {
      state.invoice = invoice;
      if (invoice.deals.length > 0) {
        state.currentDeal = invoice.deals[0];
        state.payMethod = state.currentDeal.paymentMethod;
      }
    }

    const handle3dsSubmitted = async () => {
      if (state.invoice === null) {
        return;
      }
      await loadInvoiceData(state.invoice.id);
      if (state.currentDeal !== null) {
        await updateCurrentDeal();
      }
    }

    const updateCurrentDeal = async () => {
      if (state.invoice === null || state.currentDeal === null) {
        return;
      }

      state.currentDeal = findDeal(state.currentDeal.id);
    }

    const copy = (obj: any, value: string) => {
      state.copyValue = value
      copyTextToClipboard(obj, value);
      if (state.copyHintTimeout) {
        clearTimeout(state.copyHintTimeout)
      }
      state.copyHintTimeout = setTimeout(() => {
        state.copyHintTimeout = null;
      }, 1000);
    }

    const confirmTransfer = async () => {
      if (state.invoice === null) {
        return;
      }

      if (showTransactionIdField.value) {
        if (!state.transactionId || state.transactionId.trim() === '') {
          state.transactionIdError = t('invoice.transaction-id-required') as string;
          return;
        }
      }

      if (receiptPdfRequired.value && state.receiptFile === null) {
        state.receiptError = t('invoice.receipt-pdf.required') as string;
        return;
      }

      if (state.currentDeal !== null) {
        state.invoice = await api.confirmTransfer(
          state.invoice.id,
          state.currentDeal.id,
          receiptPdfRequired.value ? state.receiptFile : null,
          showTransactionIdField.value ? state.transactionId.trim() : null
        );
      }

      await updateCurrentDeal();
    }

    const cancelDeal = async (returnUrl: string|null = null) => {
      event('invoiceCanceled', {})
      if (state.invoice === null) {
        if (returnUrl) {
          window.location.href = returnUrl;
        }
        return;
      }

      if (state.currentDeal !== null) {
        if (returnUrl) {
          await api.cancelInvoice(state.invoice.id);
          window.location.href = returnUrl;
          return;
        }

        state.invoice = await api.cancelInvoice(state.invoice.id);
      }

      router.replace({path: route.path, query: {id: state.invoice.id, lang: locale.value as string}});
      state.currentDeal = null;
      state.payMethod = null;
    }

    const cancelInvoice = async () => {
      event('invoiceCanceled', {})
      if (state.invoice === null) {
        return;
      }

      state.invoice = await api.cancelInvoice(state.invoice.id);
      if (state.invoice.cancelUrl) {
        setTimeout(() => {
          window.location.href == state.invoice?.cancelUrl
        }, 1000);
      }
    }

    const redirectToNspk = () => {
      state.waitingForRequisites = false;
      if (state.currentDeal === null || state.currentDeal.status !== DealStatus.TRANSFER_WAITING) {
        return
      }

      window.location.href = state.currentDeal.requisites.requisites
    }

    onMounted(async () => {

      await resolveApiUrl();

      const invoiceId = route.query.id as string

      if (state.invoice === null) {
        const invoice = await loadInvoiceData(invoiceId)
        const flow = invoice.flow ?? FlowType.DEFAULT;
        event(`FLOW_${flow}`, {})

        if (flowType.value === FlowType.NSPK) {
          if (state.currentDeal === null) {
            if (invoice.flowData?.options?.startDealValue === 'async') {
              state.interval = setInterval(async () => {

                state.invoice = await loadInvoiceData(invoiceId)
                if (state.invoice && state.invoice.deals.length > 0) {
                  state.currentDeal = state.invoice.deals[0]
                  state.payMethod = state.currentDeal.paymentMethod
                }

                state.startDealCount++;
                if (state.currentDeal !== null || state.startDealCount >= 30) {
                  state.interval && clearInterval(state.interval)
                  redirectToNspk();

                  return;
                }
              }, 1000)

              return;
            }

            state.waitingForRequisites = true;
            if (!singleDealStartAttempt.value || invoice.status === InvoiceStatus.NEW) {
              try {
                state.invoice = await api.startDeal(invoice.id, null, {paymentOption: 'SBP_QR'});
              } catch (e) {
              }
            }
            if (state.invoice && state.invoice.deals.length > 0) {
              state.currentDeal = state.invoice.deals[0]
              state.payMethod = state.currentDeal.paymentMethod
            }
          }

          redirectToNspk()

          return;
        }
      }

      if (paymentPageLayout.value === 'pay_buttons') {
        return;
      }

      if (state.availableMethods === null) {
        await loadAvailableMethods(invoiceId)
      }
      if (state.availableVariants === null) {
        await loadAvailableVariants(invoiceId)
      }

      if (state.payMethod === null && state.currentDeal === null && showUserPhoneForm.value === false) {
        const payMethod = route.query.method as string | undefined
        state.payMethod = payMethod ? payMethod : null
        if (state.payMethod !== null) {
          await selectPayMethod(state.payMethod)
        } else if (state.availableMethods?.length) {
          await selectPayMethod(state.availableMethods[0])
        }
      }

      updateCurrentTime()
      state.updateCurrentTimeInterval = setInterval(updateCurrentTime, 1000)
      state.interval = setInterval(async () => {
        if (state.invoice?.status === InvoiceStatus.PAID
          || state.invoice?.status === InvoiceStatus.EXPIRED
          || state.invoice?.status === InvoiceStatus.CANCELED
        ) {
          return;
        }

        await loadInvoiceData(invoiceId)
        if (state.payMethod !== null) {
          await updateCurrentDeal();
        }
        state.startDealCount++;
        if (state.userPhoneSubmitted && state.currentDeal === null) {
          const waitStartedAt = userPhoneWaitStartedAt ?? Date.now()
          if (Date.now() - waitStartedAt >= PHONE_REQUISITES_WAIT_MS) {
            state.waitingForRequisites = false
          }
          return
        }
        if (state.currentDeal === null && state.startDealCount < 3 && showUserPhoneForm.value === false && !singleDealStartAttempt.value) {
          await loadAvailableMethods(invoiceId)
          if (state.availableMethods?.length) {
            await selectPayMethod(state.availableMethods[0])
          }
        } else {
          state.waitingForRequisites = false
        }
      }, 5000)
    });

    const resolveApiUrl = async () => {
      let promises = [];
      let urls = [] as string[];
      let resolved = false;
      for (const i of [0, 4, 5, 6]) {
        const suffix = i === 0 ? '' : i.toString();
        let baseURL = window.API_URL.replace('api.', `api${suffix}.`);

        if (urls.includes(baseURL)) {
          continue;
        }

        urls.push(baseURL)
        promises.push(api.ping(baseURL).then(() => {
          if (!resolved) {
            resolved = true;
            axios.defaults.baseURL = baseURL
          }
        }));
      }

      if (promises.length === 0) {
        axios.defaults.baseURL = window.API_URL;
        return;
      }

      try {
        await Promise.any(promises);
      } catch {
        axios.defaults.baseURL = window.API_URL;
      }
    }

    const brand = computed(() => {
      return state.invoice?.flowData?.options?.brand
    })

    const brandClasses = computed(() => {
      if (!state.invoice?.flowData?.options?.brand) {
        return []
      }
      return ['branded', `brand-${state.invoice?.flowData?.options?.brand}`]
    })

    const transferTextDesktop = computed(() => {
      if (manualSbpQrAwaitingRequisites.value) {
        return 'invoice.manual-sbp-qr-waiting'
      }
      switch (flowType.value) {
        case FlowType.CARD_ACQUIRING:
        case FlowType.MANUAL_ECOM:
          const cardLikeDeal = state.invoice?.deals.find(d => d.paymentOption === flowType.value)
          if (cardLikeDeal?.status === DealStatus.TRANSFER_WAITING) {
            return 'invoice.acquiring.enter-code'
          }
          return 'invoice.acquiring.enter-card-details'
        case FlowType.CARD_ACQUIRING_REDIRECT:
          const redirectDealDesktop = state.invoice?.deals.find(d => d.paymentOption === flowType.value)
          if (redirectDealDesktop) {
            return 'invoice.acquiring.processing-title'
          }
          return 'invoice.acquiring.enter-card-details'
        case FlowType.TO_BANK_DETAILS:
          return 'invoice.make-transfer-bank-qr-text'
        case FlowType.SBP_QR:
        case FlowType.MANUAL_SBP_QR:
        case FlowType.CLICK_QR:
        case FlowType.VIET_QR:
        case FlowType.IND_QR:
        case FlowType.BR_QR:
          return 'invoice.make-transfer-qr-text'
        case FlowType.TPAY:
        case FlowType.SBERPAY:
        case FlowType.ALFAPAY:
        case FlowType.VTBPAY:
        case FlowType.GPBPAY:
        case FlowType.PSBPAY:
        case FlowType.OZONPAY:
        case FlowType.WBPAY:
        case FlowType.SOVKOMPAY:
          return 'invoice.make-transfer-qr-mobile-text'
        case FlowType.DEFAULT:
        default:
          return 'invoice.make-transfer-text'
      }
    })

    const transferTextMobile = computed(() => {
      if (manualSbpQrAwaitingRequisites.value) {
        return 'invoice.manual-sbp-qr-waiting'
      }
      switch (flowType.value) {
        case FlowType.CARD_ACQUIRING:
        case FlowType.MANUAL_ECOM:
          const cardLikeDeal = state.invoice?.deals.find(d => d.paymentOption === flowType.value)
          if (cardLikeDeal?.status === DealStatus.TRANSFER_WAITING) {
            return 'invoice.acquiring.enter-code'
          }
          return 'invoice.acquiring.enter-card-details'
        case FlowType.CARD_ACQUIRING_REDIRECT:
          const redirectDealMobile = state.invoice?.deals.find(d => d.paymentOption === flowType.value)
          if (redirectDealMobile) {
            return 'invoice.acquiring.processing-title'
          }
          return 'invoice.acquiring.enter-card-details'
        case FlowType.TO_BANK_DETAILS:
          return 'invoice.make-transfer-bank-qr-text'
        case FlowType.SBP_QR:
        case FlowType.MANUAL_SBP_QR:
        case FlowType.CLICK_QR:
        case FlowType.VIET_QR:
        case FlowType.IND_QR:
        case FlowType.BR_QR:
          return 'invoice.make-transfer-qr-text'
        case FlowType.DEFAULT:
        case FlowType.TPAY:
        case FlowType.SBERPAY:
        case FlowType.ALFAPAY:
        case FlowType.VTBPAY:
        case FlowType.GPBPAY:
        case FlowType.PSBPAY:
        case FlowType.OZONPAY:
        case FlowType.WBPAY:
        case FlowType.SOVKOMPAY:
        default:
          return 'invoice.make-transfer-text'
      }
    })

    const showTransactionIdField = computed(() => {
      return state.invoice?.isTrxIdRequiredForConfirm
    })

    const userPhoneDigits = computed(() => {
      let digits = state.userPhone.replace(/\D/g, '')
      if (digits.startsWith('8')) {
        digits = '7' + digits.slice(1)
      }

      return digits.slice(0, 11)
    })

    const showUserPhoneForm = computed(() => {
      return state.invoice?.isUserPhoneRequired === true
        && state.invoice?.status === InvoiceStatus.NEW
        && state.userPhoneSubmitted === false
        && state.currentDeal === null
        && flowType.value !== FlowType.NSPK
    })

    const formatUserPhone = (value: string): string => {
      let digits = value.replace(/\D/g, '')
      if (digits === '') {
        return ''
      }

      if (digits.startsWith('8')) {
        digits = '7' + digits.slice(1)
      }
      if (!digits.startsWith('7')) {
        digits = '7' + digits
      }

      const rest = digits.slice(1, 11)
      let formatted = '+7'
      if (rest.length > 0) {
        formatted += ' (' + rest.slice(0, 3)
      }
      if (rest.length > 3) {
        formatted += ') ' + rest.slice(3, 6)
      }
      if (rest.length > 6) {
        formatted += '-' + rest.slice(6, 8)
      }
      if (rest.length > 8) {
        formatted += '-' + rest.slice(8, 10)
      }

      return formatted
    }

    const onUserPhoneInput = () => {
      state.userPhone = formatUserPhone(state.userPhone)
      state.userPhoneError = ''
    }

    const submitUserPhone = async () => {
      if (state.invoice === null || state.userPhoneSubmitted) {
        return
      }

      if (userPhoneDigits.value.length !== 11) {
        state.userPhoneError = t('invoice.user-phone-invalid') as string
        return
      }

      state.userPhoneSubmitted = true
      state.waitingForRequisites = true
      userPhoneWaitStartedAt = Date.now()

      const invoiceId = state.invoice.id
      if (state.availableMethods === null) {
        await loadAvailableMethods(invoiceId)
      }

      await selectPayMethod(state.payMethod ?? state.availableMethods?.[0] ?? null)
    }

    const shouldShowPaymentBlock = computed(() => {
      if (flowType.value === FlowType.CARD_ACQUIRING_REDIRECT) {
        const redirectDeal = state.invoice?.deals.find(d => d.paymentOption === FlowType.CARD_ACQUIRING_REDIRECT)
        if (!redirectDeal) {
          return true
        }
        if (redirectDeal.status !== DealStatus.TRANSFER_WAITING) {
          return false
        }
        return !isAcsReturn.value
      }
      if (flowType.value === FlowType.CARD_ACQUIRING || flowType.value === FlowType.MANUAL_ECOM) {
        const cardLikeDeal = state.invoice?.deals.find(d => d.paymentOption === flowType.value)
        return !cardLikeDeal || cardLikeDeal.status === DealStatus.TRANSFER_WAITING
      }
      return state.currentDeal?.status === DealStatus.TRANSFER_WAITING
    })

    onUnmounted(() => {
      if (state.updateCurrentTimeInterval) {
        clearInterval(state.updateCurrentTimeInterval)
      }

      if (state.interval) {
        clearInterval(state.interval)
      }
    })

    const openDeeplink = (deeplinks: any) => {
      event('deeplinkClicked', {})

      state.deeplinkClicked = true;
      _openDeeplink(0, deeplinks);
    }

    const _openDeeplink = (index: number, deeplinks: any) => {
      const deeplink = deeplinks[index]
      event('deeplink', {link: deeplink})
      window.open(deeplink, "_self")
      index++

      if (!deeplinks[index]) {
        return;
      }


      setTimeout(() => {
        _openDeeplink(index++, deeplinks);
      }, 1000);
    }

    return {
      ...toRefs(state),
      psbCrossBorderDeal,
      alfaCrossBorderDeal,
      alfaC2cCrossBorderDeal,
      vaslTransferBank,
      vaslTransferDeal,
      kazakhstanCrossBorderDeal,
      paymentPageLayout,
      locale,
      currentUrl,
      openDeeplink,
      brand,
      brandClasses,
      numberFormatter,
      selectPayMethod,
      paymentMethodsHelper,
      apiUrl,
      isExpired,
      formatCancelTime,
      DealStatus,
      InvoiceStatus,
      cancelDeal,
      confirmTransfer,
      cancelInvoice,
      addAttachment,
      disputeAttachment,
      openDispute,
      getTimeToPayPercent,
      setDisputeAttachment,
      flowType,
      FlowType,
      isBankPayFlow,
      showSbpQrPaymentDetails,
      cssFlowClassName,
      title,
      copy,
      transferTextDesktop,
      transferTextMobile,
      showTransactionIdField,
      shouldShowPaymentBlock,
      showTpayWarning,
      receiptPdfRequired,
      receiptDropzone,
      removeReceipt,
      showAmountCopyButton,
      showUserPhoneForm,
      onUserPhoneInput,
      submitUserPhone,
      handleDealCreated,
      handle3dsSubmitted
    }
  }
})
